import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import GridLayout from 'react-grid-layout';
import { HighlightedSection } from 'smartsuite-ui/lib/containers/HighlightedSection';
import { PadContainer } from 'smartsuite-ui/lib/containers/PadContainer';
import { icons } from 'smartsuite-ui/lib/constants/icons';
import { HorizontalFlex } from 'smartsuite-ui/lib/containers/HorizontalFlex';
import { ButtonWithIcon } from 'smartsuite-ui/lib/forms/ButtonWithIcon';
import { ErrorAlert } from 'smartsuite-ui/lib/alerts/ErrorAlert';
import SvgImage from './svg/SvgImage';
import {
    defaultGridRows,
    defaultGridColumns,
    editorGridRowHeightPx,
    editorTabs,
} from '../shared/constants/constants';
import useWindowSize from '../shared/hooks/windowSizeHook';
import { useLabelContext } from '../shared/hooks/labelContext';

function getEditorGridLayoutStyles(gridWidth) {
    const minHeight = defaultGridRows * editorGridRowHeightPx;
    const columnWidth = gridWidth / defaultGridColumns;

    return {
        minHeight: `${minHeight}px`,
        backgroundSize: `${columnWidth}px ${editorGridRowHeightPx}px`,
    };
}

// eslint-disable-next-line complexity
export default function EditorGrid(props) {
    const { dashboardLabels } = useLabelContext();
    const windowSize = useWindowSize();
    const [gridWidth, setGridWidth] = useState(0);
    const gridRef = useRef();

    const scrollbarWidth = window.innerWidth - document.body.clientWidth;

    useLayoutEffect(() => {
        setTimeout(() => {
            setGridWidth(gridRef.current.offsetWidth);
        }, 200);
    }, [scrollbarWidth, windowSize, props.drawerCollapsed]);

    function onDrop(newLayout, layoutItem) {
        props.setUpdatedWidgets([...props.widgets, props.draggingWidget]);
        props.setGridLayout(newLayout);
        props.setDraggingWidget();
        props.setSelectedWidgetId(layoutItem.i);
        props.setActiveTab(editorTabs.editWidget);
    }

    // eslint-disable-next-line complexity
    function getWidgetClass(widgetId) {
        let baseClass = 'editor__widget-interior';
        if (widgetId === props.selectedWidgetId) {
            baseClass += ' editor__widget-interior--selected';
        }
        if (props.errorWidgetIds.find((x) => x === widgetId)) {
            baseClass += ' editor__widget-interior--error';
        }
        return baseClass;
    }

    function renderWidgetBody(widget) {
        if (props.errorWidgetIds.includes(widget.id)) {
            return (
                <ErrorAlert>{`${dashboardLabels.invalidFields}`}</ErrorAlert>
            );
        }

        return (
            <HorizontalFlex
                cssClassModifiers={['small-relative', 'justify-center']}
            >
                <SvgImage type={widget.staticData.configuration.type} />
            </HorizontalFlex>
        );
    }

    function renderWidgets() {
        return props.gridLayout.map((layoutItem) => {
            const id = layoutItem.i;
            const widget = props.widgets.find((x) => x.id === id);

            if (!widget) {
                return <div key={id} className="editor__widget" />;
            }

            return (
                <div
                    key={id}
                    className="editor__widget"
                    onClick={() => {
                        props.setSelectedWidgetId(id);
                        props.setActiveTab(editorTabs.editWidget);
                    }}
                    onTouchStart={() => {
                        props.setSelectedWidgetId(id);
                        props.setActiveTab(editorTabs.editWidget);
                    }}
                >
                    <div className={getWidgetClass(id)}>
                        <div
                            className="custom-dashboard-widget__header"
                            style={widget.staticData.headerStyles}
                        >
                            <div className="title title--size-small widget__header-text">
                                <span>{widget.stateData.title}</span>
                            </div>
                            <ButtonWithIcon
                                iconClass={icons.trash}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    props.deleteWidget(id);
                                    props.setActiveTab(
                                        editorTabs.widgetLibrary
                                    );
                                }}
                            />
                        </div>
                        {renderWidgetBody(widget)}
                    </div>
                </div>
            );
        });
    }

    return (
        <div className="editor-grid__container">
            <HighlightedSection>
                <PadContainer cssClassModifiers={['pad-medium']}>
                    <div ref={gridRef} className="editor-grid">
                        <div
                            className="editor-grid__overlay"
                            style={{ zIndex: props.draggingWidget ? 0 : 1 }}
                            onClick={() => {
                                props.setSelectedWidgetId();
                                props.setActiveTab(editorTabs.widgetLibrary);
                            }}
                        />
                        <GridLayout
                            style={getEditorGridLayoutStyles(gridWidth)}
                            className="editor-grid__layout"
                            layout={props.gridLayout}
                            onDragStop={props.setGridLayout}
                            onResizeStop={props.setGridLayout}
                            rowHeight={editorGridRowHeightPx}
                            cols={defaultGridColumns}
                            width={gridWidth}
                            compactType={null}
                            margin={[0, 0]}
                            isDroppable={true}
                            droppingItem={props.draggingWidget}
                            onDrop={onDrop}
                        >
                            {renderWidgets()}
                        </GridLayout>
                    </div>
                </PadContainer>
            </HighlightedSection>
        </div>
    );
}

EditorGrid.propTypes = {
    deleteWidget: PropTypes.func.isRequired,
    draggingWidget: PropTypes.instanceOf(Object),
    drawerCollapsed: PropTypes.bool.isRequired,
    errorWidgetIds: PropTypes.instanceOf(Array).isRequired,
    gridLayout: PropTypes.instanceOf(Array).isRequired,
    selectedWidgetId: PropTypes.string,
    setActiveTab: PropTypes.func.isRequired,
    setDraggingWidget: PropTypes.func.isRequired,
    setGridLayout: PropTypes.func.isRequired,
    setSelectedWidgetId: PropTypes.func.isRequired,
    setUpdatedWidgets: PropTypes.func.isRequired,
    widgets: PropTypes.instanceOf(Array).isRequired,
};
